import { useCallback, useLayoutEffect, useRef, type ComponentProps, type ReactNode } from "react";

import { observer } from "mobx-react-lite";

import { ExpressionNode } from "@/components/expression/ExpressionNode";
import { ITEM_GAP_PX } from "@/components/expression/inline-node/utils";
import { SupSubNode } from "@/components/expression/SupSubNode";
import { MathThemeContext } from "@/components/expression/theme";
import { hintLightTheme } from "@/components/expression/theme/corca-themes";
import { FlatNode } from "@/components/expression/utils/FlatNode";
import { NodeStyleProvider, useNodeStyle } from "@/components/expression/utils/NodeStyleProvider";
import { twMerge } from "@/utils/twMerge";
import { useResizeObserver } from "@/utils/useResizeObserver";

import type { InlineNodeContentRenderItem } from "@/components/expression/inline-node/types";
import type { InlineNodeModel } from "@/models/nodes/InlineNodeModel";

export type ItemRendererProps = {
  item: InlineNodeContentRenderItem;
  parentInline: InlineNodeModel | undefined;
  isFirstInRow: boolean;
  /**
   * Cursor placement for the item's `FlatNode`. `"none"` is used when another
   * element (e.g. a leading filler) owns the cursor anchor at this index.
   */
  cursorPosition?: "start" | "none";
  onItemSize?: (key: string, width: number) => void;
};

export const ItemRenderer = observer(function ItemRenderer({
  item,
  parentInline,
  isFirstInRow,
  cursorPosition = "start",
  onItemSize,
}: ItemRendererProps) {
  const { view } = useNodeStyle();
  const isTextView = view === "text";
  const measured = !!onItemSize;
  const withLeftMargin = !measured && !isFirstInRow && !isTextView;

  const { node: currentNode, inlineIndex, attachmentNodes, key } = item;

  const className = item.dimmed ? "opacity-50" : undefined;

  const content =
    attachmentNodes.length > 0 ? (
      <NodeStyleProvider placeholderVisible={isTextView ? "none" : "if-empty"}>
        <SupSubNode
          node={currentNode}
          index={inlineIndex}
          parentNode={parentInline}
          attachmentNodes={attachmentNodes}
          className={className}
        />
      </NodeStyleProvider>
    ) : (
      currentNode && (
        <NodeStyleProvider placeholderVisible="if-empty">
          {parentInline ? (
            <FlatNode
              cursorPosition={cursorPosition}
              inlineNode={parentInline}
              index={inlineIndex}
              className={twMerge(withLeftMargin && "ml-[1px]", className)}
              cursorClassName={inlineIndex > 0 ? "-translate-x-[1px]" : undefined}
            >
              <ExpressionNode node={currentNode} />
            </FlatNode>
          ) : (
            <ExpressionNode node={currentNode} className={className} />
          )}
        </NodeStyleProvider>
      )
    );

  const output = item.dimmed ? (
    <MathThemeContext.Provider value={hintLightTheme}>{content}</MathThemeContext.Provider>
  ) : (
    content
  );

  if (!measured) {
    return output;
  }

  return (
    <MeasuredItem
      itemKey={key}
      onSize={onItemSize}
      style={{ marginLeft: isFirstInRow ? undefined : `${ITEM_GAP_PX}px` }}
    >
      {output}
    </MeasuredItem>
  );
});

type MeasuredItemProps = ComponentProps<"div"> & {
  itemKey: string;
  children: ReactNode;
  onSize: (key: string, width: number) => void;
};

export function MeasuredItem({ itemKey, children, onSize, ...props }: MeasuredItemProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const lastWidthRef = useRef<number | null>(null);

  const handleOnSize = useCallback(
    (width: number) => {
      if (lastWidthRef.current !== width) {
        lastWidthRef.current = width;
        onSize(itemKey, width);
      }
    },
    [itemKey, onSize],
  );

  useLayoutEffect(() => {
    if (ref.current) {
      const { width } = ref.current.getBoundingClientRect();
      handleOnSize(width);
    }
  }, [handleOnSize]);

  useResizeObserver(ref, (entry) => {
    const { width } = entry.contentRect;
    handleOnSize(width);
  });

  return (
    <div ref={ref} className="flex flex-none items-baseline" {...props}>
      {children}
    </div>
  );
}
